/**
 * Modal — boîtes de dialogue non bloquantes (remplace confirm() et prompt()).
 */
import { h } from "./dom.js";

export interface ModalOptions {
	title: string;
	content: HTMLElement | string;
	/** Libellés des boutons d'action ; le dernier est mis en avant. */
	actions?: {
		label: string;
		kind?: "primary" | "danger" | "default";
		onClick: () => void | Promise<void>;
	}[];
	/** Fermer sur Échap / clic sur le fond. Défaut : true. */
	dismissable?: boolean;
}

let openOverlay: HTMLElement | null = null;

export function openModal(options: ModalOptions): void {
	closeModal();
	const dismissable = options.dismissable !== false;

	const overlay = h("div", { class: "modal-overlay", role: "dialog", "aria-modal": "true" });
	const box = h("div", { class: "modal-box" });
	box.appendChild(h("div", { class: "modal-title" }, options.title));
	if (typeof options.content === "string") {
		box.appendChild(h("p", null, options.content));
	} else {
		box.appendChild(options.content);
	}

	if (options.actions && options.actions.length > 0) {
		const actionsRow = h("div", { class: "modal-actions" });
		for (const action of options.actions) {
			const kindClass =
				action.kind === "primary" ? " btn-primary" : action.kind === "danger" ? " btn-danger" : "";
			actionsRow.appendChild(
				h(
					"button",
					{
						class: `btn${kindClass}`,
						onclick: async () => {
							await action.onClick();
						},
					},
					action.label
				)
			);
		}
		box.appendChild(actionsRow);
	}

	overlay.appendChild(box);
	overlay.addEventListener("mousedown", (event) => {
		if (dismissable && event.target === overlay) closeModal();
	});
	document.addEventListener("keydown", onKeydown);
	openOverlay = overlay;
	document.body.appendChild(overlay);
}

export function closeModal(): void {
	if (!openOverlay) return;
	document.removeEventListener("keydown", onKeydown);
	openOverlay.remove();
	openOverlay = null;
}

function onKeydown(event: KeyboardEvent): void {
	if (event.key === "Escape") closeModal();
}

/**
 * Confirmation destructive — remplace window.confirm().
 * Résout true si l'utilisateur confirme.
 */
export function confirmDialog(title: string, message: string): Promise<boolean> {
	return new Promise((resolve) => {
		const finish = (value: boolean) => {
			closeModal();
			resolve(value);
		};
		openModal({
			title,
			content: message,
			actions: [
				{ label: "Annuler", onClick: () => finish(false) },
				{ label: "Confirmer", kind: "danger", onClick: () => finish(true) },
			],
		});
	});
}

/** Entrée de texte — remplace window.prompt(). Résout null si annulé. */
export function promptDialog(
	title: string,
	placeholder = "",
	initial = ""
): Promise<string | null> {
	return new Promise((resolve) => {
		const input = h("input", { type: "text", value: initial, placeholder }) as HTMLInputElement;
		const finish = (value: string | null) => {
			closeModal();
			resolve(value);
		};
		input.addEventListener("keydown", (event) => {
			if (event.key === "Enter") finish(input.value.trim() || null);
		});
		openModal({
			title,
			content: input,
			actions: [
				{ label: "Annuler", onClick: () => finish(null) },
				{ label: "OK", kind: "primary", onClick: () => finish(input.value.trim() || null) },
			],
		});
		input.focus();
	});
}
